import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";

interface ManualEntryFormProps {
  county: string;
  onRecordSaved: () => void;
}

export const ManualEntryForm = ({ county, onRecordSaved }: ManualEntryFormProps) => {
  const [formData, setFormData] = useState({
    document_number: '',
    filing_date: '',
    grantor_name: '',
    grantee_name: '',
    grantee_address: '',
    property_address: '',
    loan_amount: '',
    interest_rate: '',
    monthly_payment: '',
    term_months: '',
    notes: ''
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.document_number || !formData.filing_date) {
      toast.error("Document number and filing date are required");
      return;
    }

    setIsSaving(true);

    try {
      const hasCompleteAddress = Boolean(
        formData.grantee_address && 
        formData.grantee_address.trim().length > 10
      );

      const { data, error } = await supabase
        .from('county_research')
        .insert({
          county: county.toLowerCase(),
          document_number: formData.document_number,
          filing_date: formData.filing_date,
          grantor_name: formData.grantor_name || null,
          grantee_name: formData.grantee_name || null,
          grantee_address: formData.grantee_address || null,
          property_address: formData.property_address || null,
          loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null,
          interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
          monthly_payment: formData.monthly_payment ? parseFloat(formData.monthly_payment) : null,
          term_months: formData.term_months ? parseInt(formData.term_months) : null,
          notes: formData.notes || null,
          status: hasCompleteAddress ? 'ready_for_melissa' : 'new',
          address_extraction_method: 'manual_entry',
          address_confidence_score: hasCompleteAddress ? 100 : null,
          address_needs_review: !hasCompleteAddress,
          is_potential_owner_finance: true
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(`Record saved successfully${hasCompleteAddress ? ' - Ready for Melissa Data' : ''}`);
      
      // Reset form
      setFormData({
        document_number: '',
        filing_date: '',
        grantor_name: '',
        grantee_name: '',
        grantee_address: '',
        property_address: '',
        loan_amount: '',
        interest_rate: '',
        monthly_payment: '',
        term_months: '',
        notes: ''
      });

      onRecordSaved();
    } catch (error) {
      console.error("Error saving record:", error);
      toast.error("Failed to save record");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Manual Entry - {county}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="document_number">Document Number *</Label>
              <Input
                id="document_number"
                value={formData.document_number}
                onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                placeholder="e.g., 2024-001234"
                required
              />
            </div>
            <div>
              <Label htmlFor="filing_date">Filing Date *</Label>
              <Input
                id="filing_date"
                type="date"
                value={formData.filing_date}
                onChange={(e) => setFormData({ ...formData, filing_date: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="grantor_name">Seller/Grantor Name</Label>
              <Input
                id="grantor_name"
                value={formData.grantor_name}
                onChange={(e) => setFormData({ ...formData, grantor_name: e.target.value })}
                placeholder="Seller name"
              />
            </div>
            <div>
              <Label htmlFor="grantee_name">Buyer/Grantee Name</Label>
              <Input
                id="grantee_name"
                value={formData.grantee_name}
                onChange={(e) => setFormData({ ...formData, grantee_name: e.target.value })}
                placeholder="Buyer name"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="grantee_address">Seller Address (for contact)</Label>
            <Input
              id="grantee_address"
              value={formData.grantee_address}
              onChange={(e) => setFormData({ ...formData, grantee_address: e.target.value })}
              placeholder="Complete address will auto-flag for Melissa Data cleanup"
            />
          </div>

          <div>
            <Label htmlFor="property_address">Property Address</Label>
            <Input
              id="property_address"
              value={formData.property_address}
              onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
              placeholder="Property location"
            />
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label htmlFor="loan_amount">Loan Amount</Label>
              <Input
                id="loan_amount"
                type="number"
                step="0.01"
                value={formData.loan_amount}
                onChange={(e) => setFormData({ ...formData, loan_amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="interest_rate">Interest Rate %</Label>
              <Input
                id="interest_rate"
                type="number"
                step="0.01"
                value={formData.interest_rate}
                onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="monthly_payment">Monthly Payment</Label>
              <Input
                id="monthly_payment"
                type="number"
                step="0.01"
                value={formData.monthly_payment}
                onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="term_months">Term (Months)</Label>
              <Input
                id="term_months"
                type="number"
                value={formData.term_months}
                onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                placeholder="360"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows={3}
            />
          </div>

          <Button type="submit" disabled={isSaving} className="w-full">
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Record'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
